import React, { useState } from "react";
import "./Password.scss";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button, Col, Input, Tooltip, Tabs } from "antd";
import Validates from "@utils/Validates";
import { useUser } from "contexts/UserContext";
import Helpers from "@utils/Helpers";
import userApi from "services/UserApi";
import { errorLog } from "logging";
import SearchSetting from "@components/SearchSetting/SearchSetting";
import { Link } from "react-router-dom";

interface IPassword {
  setIsUpdating: (is: boolean) => void;
}

const Password: React.FC<IPassword> = ({ setIsUpdating }) => {
  const [passwordGroup, setPasswordGroup] = useState({
    currentPassword: "",
    newPassword: "",
    retypePassword: "",
  });

  const [errorMessages, setErrorMessages] = useState({
    currentPassword: "",
    newPassword: "",
    retypePassword: "",
  });

  const { userProfile } = useUser();

  const hint = (
    <span>
      Password must be at least 8 characters long and include at least one
      UPPERCASE letter, one lowercase letter, one number, and one special
      character.
    </span>
  );

  const handleInputChange = (key: string, value: string) => {
    setPasswordGroup({ ...passwordGroup, [key]: value });
    setErrorMessages({ ...errorMessages, [key]: "" });
  };

  const handleSubmitChangePassword = async () => {
    const { currentPassword, newPassword, retypePassword } = passwordGroup;

    if (!Validates.validateValidPassword(currentPassword)) {
      Helpers.notification.warning("Current password is not valid");
      return;
    }

    if (!Validates.validateValidPassword(newPassword)) {
      Helpers.notification.warning("New password is not valid");
      return;
    }

    if (newPassword === currentPassword) {
      Helpers.notification.warning(
        "New password cannot be the same as current"
      );
      return;
    }

    if (newPassword !== retypePassword) {
      Helpers.notification.warning(
        "New password and retype password do not match"
      );
      return;
    }
    setIsUpdating(true);
    const body = {
      old_password: passwordGroup.currentPassword,
      new_password: passwordGroup.newPassword,
    };

    try {
      const response = await userApi.changePassword(body);
      console.log(response);
      if (
        response.status === 200 &&
        response.data.message === "Password changed successfully"
      ) {
        setIsUpdating(false);
        Helpers.showSuccessUpdated("Update password successfully");
        setPasswordGroup({
          currentPassword: "",
          newPassword: "",
          retypePassword: "",
        });
      }
    } catch (error: any) {
      setIsUpdating(false);
      if (error.status === 400) {
        Helpers.notification.warning("Current password is incorrect.");
        return;
      }
      Helpers.notification.error("Cannot change password. Please try again");
      errorLog("Password.tsx - handleSubmitChangePassword", error);
    }
  };

  const handleSubmitCreatePassword = () => {
    const { newPassword, retypePassword } = passwordGroup;
    let errors: any = {};

    if (!Validates.validateValidPassword(newPassword)) {
      Helpers.notification.warning("New password is not valid");
      return;
    }

    if (newPassword !== retypePassword) {
      Helpers.notification.warning(
        "New password and retype password do not match"
      );
      return;
    }

    setErrorMessages(errors);

    if (Object.keys(errors).length === 0) {
      Helpers.showSuccessUpdated("Create password successfully");
    }
  };

  return (
    <div className="Password">
      <SearchSetting />
      <div className="title">Password</div>

      <Tabs
        className="tab"
        defaultActiveKey="1"
        items={[
          {
            key: "1",
            label: "Change Password",
            children: (
              <Col span={12}>
                <div className="form-group">
                  <div className="label">
                    Current Password
                    <Tooltip placement="right" title={hint}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                  <Input.Password
                    size="large"
                    value={passwordGroup.currentPassword}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    onChange={(e) =>
                      handleInputChange("currentPassword", e.target.value)
                    }
                  />
                  {errorMessages.currentPassword && (
                    <div className="error-message">
                      {errorMessages.currentPassword}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <div className="label">
                    New Password
                    <Tooltip placement="right" title={hint}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                  <Input.Password
                    size="large"
                    value={passwordGroup.newPassword}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    onChange={(e) =>
                      handleInputChange("newPassword", e.target.value)
                    }
                  />
                  {errorMessages.newPassword && (
                    <div className="error-message">
                      {errorMessages.newPassword}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <div className="label">
                    Retype New Password
                    <Tooltip placement="right" title={hint}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                  <Input.Password
                    size="large"
                    value={passwordGroup.retypePassword}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    onChange={(e) =>
                      handleInputChange("retypePassword", e.target.value)
                    }
                  />
                  {errorMessages.retypePassword && (
                    <div className="error-message">
                      {errorMessages.retypePassword}
                    </div>
                  )}
                </div>

                <div className="form-footer">
                  <Link to="/forgot-password" className="forgot-password">
                    Forgot password?
                  </Link>
                  <Button
                    className="primary"
                    onClick={handleSubmitChangePassword}
                  >
                    Save
                  </Button>
                </div>
              </Col>
            ),
          },
          {
            key: "2",
            label: `Create Password`,
            children: (
              <Col span={12}>
                <div className="form-group">
                  <div className="label">
                    New Password
                    <Tooltip placement="right" title={hint}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                  <Input.Password
                    size="large"
                    value={passwordGroup.newPassword}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    onChange={(e) =>
                      handleInputChange("newPassword", e.target.value)
                    }
                  />
                  {errorMessages.newPassword && (
                    <div className="error-message">
                      {errorMessages.newPassword}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <div className="label">
                    Retype New Password
                    <Tooltip placement="right" title={hint}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                  <Input.Password
                    size="large"
                    value={passwordGroup.retypePassword}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    onChange={(e) =>
                      handleInputChange("retypePassword", e.target.value)
                    }
                  />
                  {errorMessages.retypePassword && (
                    <div className="error-message">
                      {errorMessages.retypePassword}
                    </div>
                  )}
                </div>

                <div className="form-footer">
                  <Button
                    className="primary"
                    onClick={handleSubmitCreatePassword}
                  >
                    Save
                  </Button>
                </div>
              </Col>
            ),
            disabled: userProfile?.google_id === "",
          },
        ]}
      />

      {/* <Col span={12}>
        {userProfile?.google_id === "" && (
          <div className="form-group">
            <div className="label">
              Current Password
              <Tooltip placement="right" title={hint}>
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <Input.Password
              size="large"
              value={passwordGroup.currentPassword}
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              onChange={(e) =>
                handleInputChange("currentPassword", e.target.value)
              }
            />
            {errorMessages.currentPassword && (
              <div className="error-message">
                {errorMessages.currentPassword}
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <div className="label">
            Current Password
            <Tooltip placement="right" title={hint}>
              <InfoCircleOutlined />
            </Tooltip>
          </div>
          <Input.Password
            size="large"
            value={passwordGroup.currentPassword}
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            onChange={(e) =>
              handleInputChange("currentPassword", e.target.value)
            }
          />
          {errorMessages.currentPassword && (
            <div className="error-message">{errorMessages.currentPassword}</div>
          )}
        </div>

        <div className="form-group">
          <div className="label">
            New Password
            <Tooltip placement="right" title={hint}>
              <InfoCircleOutlined />
            </Tooltip>
          </div>
          <Input.Password
            size="large"
            value={passwordGroup.newPassword}
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            onChange={(e) => handleInputChange("newPassword", e.target.value)}
          />
          {errorMessages.newPassword && (
            <div className="error-message">{errorMessages.newPassword}</div>
          )}
        </div>

        <div className="form-group">
          <div className="label">
            Retype New Password
            <Tooltip placement="right" title={hint}>
              <InfoCircleOutlined />
            </Tooltip>
          </div>
          <Input.Password
            size="large"
            value={passwordGroup.retypePassword}
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            onChange={(e) =>
              handleInputChange("retypePassword", e.target.value)
            }
          />
          {errorMessages.retypePassword && (
            <div className="error-message">{errorMessages.retypePassword}</div>
          )}
        </div>

        <div className="form-footer">
          <a className="forgot-password" href="#">
            Forgot password?
          </a>
          <Button className="primary" onClick={handleSubmitCreatePassword}>
            Save
          </Button>
        </div>
      </Col> */}
    </div>
  );
};

export default Password;
